import classNames from "@/common/utils/classNames";
import ProfileHeroPhoto from "./ProfileHeroPhoto";

// Toolbar buttons use the site's app-bar metrics: 48px round targets, no glass, page colour behind them.
export const GLASS_BASE =
  "inline-flex h-12 min-w-12 cursor-pointer items-center justify-center gap-1.5 rounded-full px-3 text-[15px] font-medium text-primary no-underline transition-colors active:bg-black/[0.06]";

// icon-only (round) toolbar button
export const GLASS_ROUND = "w-12 min-w-12 p-0 !text-[#222]";

// kept for callers that still pass the old modifier class
const LEGACY_ROUND = "profile-hero__glass-btn--round";

function glassClass(className) {
  const round = className.includes(LEGACY_ROUND);
  return classNames(GLASS_BASE, round && GLASS_ROUND, className);
}

export function ProfileHeroGlassButton({ className = "", children, ...props }) {
  return (
    <button type="button" className={glassClass(className)} {...props}>
      {children}
    </button>
  );
}

export function ProfileHeroGlassLink({ className = "", children, ...props }) {
  return (
    <a className={glassClass(className)} {...props}>
      {children}
    </a>
  );
}

// Telegram profile actions: equal white tiles, accent icon over a small accent label
const ACTION_BASE =
  "flex h-[62px] min-w-0 flex-1 basis-0 cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border-0 px-1.5 no-underline transition-colors disabled:cursor-not-allowed disabled:opacity-55 [&_svg]:h-6 [&_svg]:w-6";

const ACTION_VARIANTS = {
  glass: "bg-white text-primary active:enabled:bg-slate-100",
  primary: "bg-white text-primary active:enabled:bg-slate-100",
};

function actionClass(variant, className) {
  return classNames(ACTION_BASE, ACTION_VARIANTS[variant] || ACTION_VARIANTS.glass, className);
}

function ActionIcon({ icon, variant }) {
  if (!icon) return null;
  return (
    <span
      className={classNames(
        "inline-flex shrink-0 items-center justify-center",
        "text-primary"
      )}
      aria-hidden
    >
      {icon}
    </span>
  );
}

function ActionLabel({ label, variant }) {
  return (
    <span
      className={classNames(
        "max-w-full overflow-hidden text-ellipsis whitespace-nowrap text-[12px] font-medium leading-4 text-primary",
        variant === "primary" && "font-semibold"
      )}
    >
      {label}
    </span>
  );
}

export function ProfileHeroAction({
  icon,
  label,
  onClick,
  disabled = false,
  title,
  variant = "glass",
  className = "",
  "aria-label": ariaLabel,
}) {
  return (
    <button
      type="button"
      className={actionClass(variant, className)}
      onClick={onClick}
      disabled={disabled}
      title={title || label}
      aria-label={ariaLabel || label}
    >
      <ActionIcon icon={icon} variant={variant} />
      {label ? <ActionLabel label={label} variant={variant} /> : null}
    </button>
  );
}

export function ProfileHeroActionLink({ href, icon, label, title, className = "", variant = "glass" }) {
  return (
    <a
      className={actionClass(variant, className)}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      title={title || label}
      aria-label={label}
    >
      <ActionIcon icon={icon} variant={variant} />
      <ActionLabel label={label} variant={variant} />
    </a>
  );
}

export default function ProfileHeroShell({
  remoteId,
  fullName,
  avatarUrl,
  avatarAlt = "",
  toolbarStart = null,
  toolbarEnd = null,
  name,
  subtitle = null,
  actions = null,
  tags = null,
  className = "",
}) {
  return (
    <section
      className={classNames("profile-hero relative z-[1] shrink-0 bg-[#F2F2F2]", className)}
      aria-label="هدر پروفایل"
    >
      <div
        data-app-bar=""
        className="sticky top-0 z-[3] box-content flex h-14 items-center justify-between gap-2 bg-[#F2F2F2] px-1 pt-[env(safe-area-inset-top)]"
      >
        <div className="flex items-center gap-1">{toolbarStart}</div>
        <div className="flex items-center gap-1">{toolbarEnd}</div>
      </div>

      <div className="mx-auto flex max-w-lg flex-col items-center px-3 pb-3 pt-1 text-center">
        <div className="relative size-[90px] overflow-hidden rounded-full bg-slate-200">
          <ProfileHeroPhoto remoteId={remoteId} fullName={fullName} avatarUrl={avatarUrl} alt={avatarAlt} />
        </div>

        <div className="mt-3 min-w-0">
          {name ? <h1 className="m-0 text-[20px] font-semibold leading-8 text-[#222]">{name}</h1> : null}
          {subtitle ? <p className="mx-0 mb-0 mt-0.5 text-[13px] leading-5 text-[#8a8a8a]">{subtitle}</p> : null}
        </div>

        {actions ? (
          <div className="mt-4 flex w-full items-stretch gap-2">
            {actions}
          </div>
        ) : null}
        {tags ? <div className="mt-2.5">{tags}</div> : null}
      </div>
    </section>
  );
}
